"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PluggableLineChart = void 0;
var tslib_1 = require("tslib");
// (C) 2019 GoodData Corporation
var sdk_ui_1 = require("@gooddata/sdk-ui");
var react_1 = tslib_1.__importDefault(require("react"));
var react_dom_1 = require("react-dom");
var axis_1 = require("../../../constants/axis");
var bucket_1 = require("../../../constants/bucket");
var supportedProperties_1 = require("../../../constants/supportedProperties");
var uiConfig_1 = require("../../../constants/uiConfig");
var bucketConfig_1 = require("../../../utils/bucketConfig");
var bucketHelper_1 = require("../../../utils/bucketHelper");
var propertiesHelper_1 = require("../../../utils/propertiesHelper");
var sort_1 = require("../../../utils/sort");
var lineChartUiConfigHelper_1 = require("../../../utils/uiConfigHelpers/lineChartUiConfigHelper");
var LineChartBasedConfigurationPanel_1 = tslib_1.__importDefault(require("../../configurationPanels/LineChartBasedConfigurationPanel"));
var PluggableBaseChart_1 = require("../baseChart/PluggableBaseChart");
var cloneDeep_1 = tslib_1.__importDefault(require("lodash/cloneDeep"));
var set_1 = tslib_1.__importDefault(require("lodash/set"));
var drillDownUtil_1 = require("../drillDownUtil");
var PluggableLineChart = /** @class */ (function (_super) {
    tslib_1.__extends(PluggableLineChart, _super);
    function PluggableLineChart(props) {
        var _this = _super.call(this, props) || this;
        // set default to DUAL to get the full supported props list
        // and will be updated in getExtendedReferencePoint
        _this.axis = axis_1.AXIS.DUAL;
        _this.type = sdk_ui_1.VisualizationTypes.LINE;
        _this.supportedPropertiesList = _this.getSupportedPropertiesList();
        _this.initializeProperties(props.visualizationProperties);
        return _this;
    }
    PluggableLineChart.prototype.getSupportedPropertiesList = function () {
        return supportedProperties_1.LINE_CHART_SUPPORTED_PROPERTIES[this.axis];
    };
    PluggableLineChart.prototype.configureBucketsWithMultipleDates = function (newReferencePoint) {
        var _a;
        var buckets = (_a = newReferencePoint === null || newReferencePoint === void 0 ? void 0 : newReferencePoint.buckets) !== null && _a !== void 0 ? _a : [];
        var measures = bucketHelper_1.getMeasureItems(buckets);
        var masterMeasures = bucketHelper_1.filterOutDerivedMeasures(measures);
        var attributes = [];
        var stacks = bucketHelper_1.getStackItems(buckets, [bucket_1.ATTRIBUTE, bucket_1.DATE]);
        var allAttributes = bucketHelper_1.getAllAttributeItemsWithPreference(buckets, [
            sdk_ui_1.BucketNames.LOCATION,
            sdk_ui_1.BucketNames.TREND,
            sdk_ui_1.BucketNames.VIEW,
            sdk_ui_1.BucketNames.ATTRIBUTES,
            sdk_ui_1.BucketNames.SEGMENT,
            sdk_ui_1.BucketNames.STACK,
        ]);
        var firstDateItemInViews = bucketHelper_1.getFistDateItemWithMultipleDates(buckets);
        if (firstDateItemInViews) {
            attributes = [firstDateItemInViews];
            var nextAttribute = allAttributes.find(function (attr) { return attr !== firstDateItemInViews; });
            if (masterMeasures.length <= 1 && nextAttribute && !stacks.length) {
                stacks = [nextAttribute];
            }
        }
        else {
            if (masterMeasures.length <= 1 && allAttributes.length > 1 && !stacks.length) {
                stacks = allAttributes.slice(1, 2);
            }
            attributes = bucketHelper_1.getAttributeItemsWithoutStacks(buckets, [bucket_1.ATTRIBUTE, bucket_1.DATE]).slice(0, 1);
        }
        set_1.default(newReferencePoint, bucket_1.BUCKETS, [
            {
                localIdentifier: sdk_ui_1.BucketNames.MEASURES,
                items: bucketHelper_1.getFilteredMeasuresForStackedCharts(buckets),
            },
            {
                localIdentifier: sdk_ui_1.BucketNames.TREND,
                items: attributes,
            },
            {
                localIdentifier: sdk_ui_1.BucketNames.SEGMENT,
                items: stacks,
            },
        ]);
    };
    PluggableLineChart.prototype.configureBuckets = function (newReferencePoint) {
        var _a;
        if (this.isMultipleDatesEnabled()) {
            this.configureBucketsWithMultipleDates(newReferencePoint);
            return;
        }
        var buckets = (_a = newReferencePoint === null || newReferencePoint === void 0 ? void 0 : newReferencePoint.buckets) !== null && _a !== void 0 ? _a : [];
        var measures = bucketHelper_1.getMeasureItems(buckets);
        var masterMeasures = bucketHelper_1.filterOutDerivedMeasures(measures);
        var attributes = [];
        var stacks = bucketHelper_1.getStackItems(buckets);
        var dateItems = bucketHelper_1.getDateItems(buckets);
        var allAttributes = bucketHelper_1.getAllAttributeItemsWithPreference(buckets, [
            sdk_ui_1.BucketNames.LOCATION,
            sdk_ui_1.BucketNames.TREND,
            sdk_ui_1.BucketNames.VIEW,
            sdk_ui_1.BucketNames.SEGMENT,
            sdk_ui_1.BucketNames.STACK,
        ]);
        if (dateItems.length) {
            attributes = dateItems.slice(0, 1);
            stacks =
                masterMeasures.length <= 1 && allAttributes.length > 1
                    ? allAttributes
                        .filter(function (attribute) { return !bucketHelper_1.isDateBucketItem(attribute); })
                        .slice(0, 1)
                    : stacks;
        }
        else {
            if (masterMeasures.length <= 1 &&
                allAttributes.length > 1 &&
                !bucketHelper_1.isDateBucketItem(allAttributes === null || allAttributes === void 0 ? void 0 : allAttributes[1])) {
                stacks = allAttributes.slice(1, 2);
            }
            attributes = bucketHelper_1.getAttributeItemsWithoutStacks(buckets).slice(0, 1);
        }
        set_1.default(newReferencePoint, bucket_1.BUCKETS, [
            {
                localIdentifier: sdk_ui_1.BucketNames.MEASURES,
                items: bucketHelper_1.getFilteredMeasuresForStackedCharts(buckets),
            },
            {
                localIdentifier: sdk_ui_1.BucketNames.TREND,
                items: attributes,
            },
            {
                localIdentifier: sdk_ui_1.BucketNames.SEGMENT,
                items: stacks,
            },
        ]);
    };
    PluggableLineChart.prototype.getUiConfig = function () {
        var config = this.isMultipleDatesEnabled()
            ? uiConfig_1.LINE_UICONFIG_WITH_MULTIPLE_DATES
            : uiConfig_1.DEFAULT_LINE_UICONFIG;
        return cloneDeep_1.default(config);
    };
    PluggableLineChart.prototype.getExtendedReferencePoint = function (referencePoint) {
        var _a, _b;
        var clonedReferencePoint = cloneDeep_1.default(referencePoint);
        var newReferencePoint = tslib_1.__assign(tslib_1.__assign({}, clonedReferencePoint), { uiConfig: this.getUiConfig() });
        this.configureBuckets(newReferencePoint);
        newReferencePoint = propertiesHelper_1.setSecondaryMeasures(newReferencePoint, axis_1.AXIS_NAME.SECONDARY_Y);
        this.axis = (_b = (_a = newReferencePoint === null || newReferencePoint === void 0 ? void 0 : newReferencePoint.uiConfig) === null || _a === void 0 ? void 0 : _a.axis) !== null && _b !== void 0 ? _b : axis_1.AXIS.PRIMARY;
        this.supportedPropertiesList = this.getSupportedPropertiesList();
        newReferencePoint = lineChartUiConfigHelper_1.setLineChartUiConfig(newReferencePoint, this.intl, this.type);
        newReferencePoint = bucketConfig_1.configurePercent(newReferencePoint, false);
        newReferencePoint = bucketConfig_1.configureOverTimeComparison(newReferencePoint, !!this.featureFlags["enableWeekFilters"]);
        newReferencePoint = propertiesHelper_1.getReferencePointWithSupportedProperties(newReferencePoint, this.supportedPropertiesList);
        newReferencePoint = sort_1.removeSort(newReferencePoint);
        return Promise.resolve(bucketHelper_1.sanitizeFilters(newReferencePoint));
    };
    PluggableLineChart.prototype.addFilters = function (source, drillConfig, event) {
        var cutIntersection = drillDownUtil_1.reverseAndTrimIntersection(drillConfig, event.drillContext.intersection);
        return drillDownUtil_1.addIntersectionFiltersToInsight(source, cutIntersection);
    };
    PluggableLineChart.prototype.getInsightWithDrillDownApplied = function (source, drillDownContext) {
        var withFilters = this.addFilters(source, drillDownContext.drillDefinition, drillDownContext.event);
        return drillDownUtil_1.modifyBucketsAttributesForDrillDown(withFilters, drillDownContext.drillDefinition);
    };
    PluggableLineChart.prototype.renderConfigurationPanel = function (insight) {
        if (document.querySelector(this.configPanelElement)) {
            react_dom_1.render(react_1.default.createElement(LineChartBasedConfigurationPanel_1.default, { locale: this.locale, references: this.references, properties: this.visualizationProperties, propertiesMeta: this.propertiesMeta, insight: insight, colors: this.colors, pushData: this.handlePushData, type: this.type, isError: this.getIsError(), isLoading: this.isLoading, featureFlags: this.featureFlags, axis: this.axis }), document.querySelector(this.configPanelElement));
        }
    };
    return PluggableLineChart;
}(PluggableBaseChart_1.PluggableBaseChart));
exports.PluggableLineChart = PluggableLineChart;
//# sourceMappingURL=PluggableLineChart.js.map